import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import type { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import * as THREE from 'three'

export interface ChunkBuffer {
  chunks: string[]
  totalChunks: number
  receivedChunks: number
}

export interface ParsedMessage {
  type?: string
  __chunkType?: 'partial'
  __messageId?: string
  __totalChunks?: number
  __chunkIndex?: number
  __chunkData?: string
  [key: string]: unknown
}

export interface MessageHandler {
  type: string
  requiredData: string[]
  callback: (
    json: Record<string, unknown>,
    api: KestrelViewerMessageCallbackAPI
  ) => void
}

export const processChunkedMessage = (
  messageId: string,
  messageChunks: Map<string, ChunkBuffer>,
  dataJson: {
    __totalChunks: number
    __chunkIndex: number
    __chunkData: string
  }
): string | null => {
  if (!messageChunks.has(messageId)) {
    messageChunks.set(messageId, {
      chunks: new Array(dataJson.__totalChunks),
      totalChunks: dataJson.__totalChunks,
      receivedChunks: 0,
    })
  }

  const buffer = messageChunks.get(messageId)!
  buffer.chunks[dataJson.__chunkIndex] = dataJson.__chunkData
  buffer.receivedChunks++

  if (buffer.receivedChunks === buffer.totalChunks) {
    const completeMessage = buffer.chunks.join('')
    messageChunks.delete(messageId)
    return completeMessage
  }

  return null
}

export const handleMessageWithHandlers = (
  messageJson: ParsedMessage,
  messageHandlers: MessageHandler[],
  kestrelViewerAPI: KestrelViewerMessageCallbackAPI
) => {
  if (messageJson.type === undefined) return

  for (const handler of messageHandlers) {
    if (handler.type !== messageJson.type) continue
    if (
      handler.requiredData.filter((r: string) => messageJson[r] === undefined)
        .length > 0
    )
      continue
    handler.callback(messageJson as Record<string, unknown>, kestrelViewerAPI)
  }
}

export const parseMessage = (dataMessage: string): ParsedMessage | null => {
  try {
    return JSON.parse(dataMessage) as ParsedMessage
  } catch {
    return null
  }
}

/**
 * Chunks a message into smaller pieces for transmission
 * @param message - The message to chunk
 * @param chunkSize - The maximum size of each chunk in bytes, defaults to 15kb
 * @returns Array of chunked messages
 */
export const chunkMessage = (
  message: string | object,
  chunkSize: number = 15360
): ParsedMessage[] => {
  const messageString =
    typeof message === 'string' ? message : JSON.stringify(message)
  const messageBytes = new TextEncoder().encode(messageString)

  if (messageBytes.length <= chunkSize) {
    return [message as ParsedMessage]
  }

  const chunks: ParsedMessage[] = []
  const messageId = THREE.MathUtils.generateUUID()
  const totalChunks = Math.ceil(messageBytes.length / chunkSize)

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize
    const end = Math.min(start + chunkSize, messageBytes.length)
    const chunkBytes = messageBytes.slice(start, end)
    const chunkData = new TextDecoder().decode(chunkBytes)

    chunks.push({
      __chunkType: 'partial',
      __messageId: messageId,
      __totalChunks: totalChunks,
      __chunkIndex: i,
      __chunkData: chunkData,
    } as ParsedMessage)
  }

  return chunks
}

/**
 * Emits a UI interaction to the Pixel Streaming instance
 */
export const emitPixelStreamUIInteraction = (
  pixelStreaming: PixelStreaming,
  interaction: string | object
) => {
  let success = true
  const chunks = chunkMessage(interaction)

  chunks.forEach((chunk) => {
    success = success && pixelStreaming.emitUIInteraction(chunk)
  })

  return success
}
